import React, { useState, useEffect } from 'react';
import { ListFilter, Pencil, X } from 'lucide-react';
import api from '../api/api';
import {
  Confirm,
  Message,
  ErrorState,
  SearchableSelect,
  TableSkeleton,
  Modal,
} from '../components';
import { PageHeader } from '../components/layout';
import {
  getJiraFieldDisplayName,
  matchesJiraFieldSearch
} from '../utils/jiraFieldNames';

const FieldsPage = () => {
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [optionsField, setOptionsField] = useState(null);
  const [optionsDraft, setOptionsDraft] = useState([]);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [syncingFieldId, setSyncingFieldId] = useState(null);
  const [showExistingFieldSyncModal, setShowExistingFieldSyncModal] = useState(false);
  const [showNewFieldSyncModal, setShowNewFieldSyncModal] = useState(false);
  const [syncProjectKeys, setSyncProjectKeys] = useState('');
  const [syncIssueTypes, setSyncIssueTypes] = useState('');
  const [referenceIssueKey, setReferenceIssueKey] = useState('');
  const [projectFields, setProjectFields] = useState([]);
  const [projectFieldsLoading, setProjectFieldsLoading] = useState(false);
  const [existingFieldSearchKeyword, setExistingFieldSearchKeyword] = useState('');
  const [newFieldSearchKeyword, setNewFieldSearchKeyword] = useState('');
  const [selectedExistingFieldIds, setSelectedExistingFieldIds] = useState([]);
  const [selectedProjectFieldIds, setSelectedProjectFieldIds] = useState([]);

  // 弹窗状态
  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', onConfirm: null });
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      setLoading(true);
      const response = await api.getFields();
      const data = response.data;
      if (Array.isArray(data)) {
        setFields(data);
      } else if (data && Array.isArray(data.fields)) {
        setFields(data.fields);
      } else {
        setFields([]);
        console.warn('API返回的字段数据不是数组:', data);
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setFields([]);
      setLoading(false);
    }
  };

  const syncSingleJiraField = async (fieldId) => {
    const projectKeys = syncProjectKeys.trim();
    if (!projectKeys) {
      setAlertState({ isOpen: true, message: '请输入 Jira 项目 KEY' });
      return false;
    }

    const response = await api.syncJiraField({
      project_keys: projectKeys,
      issue_types: syncIssueTypes.trim(),
      reference_issue_key: referenceIssueKey.trim(),
      field_id: fieldId
    });
    return response.data;
  };

  const loadProjectFields = async () => {
    const projectKeys = syncProjectKeys.trim();
    if (!projectKeys) {
      setAlertState({ isOpen: true, message: '请输入 Jira 项目 KEY' });
      return;
    }

    try {
      setProjectFieldsLoading(true);
      const response = await api.getJiraProjectFields(projectKeys.split(/[,\s]+/).filter(Boolean)[0]);
      setProjectFields(response.data?.fields || []);
      setSelectedProjectFieldIds([]);
      setAlertState({
        isOpen: true,
        message: `已拉取 ${response.data?.fields?.length || 0} 个 Jira 字段`
      });
    } catch (err) {
      setAlertState({ isOpen: true, message: '拉取字段失败: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setProjectFieldsLoading(false);
    }
  };

  const toggleSelectedValue = (value, selectedValues, setSelectedValues) => {
    setSelectedValues(
      selectedValues.includes(value)
        ? selectedValues.filter(item => item !== value)
        : [...selectedValues, value]
    );
  };

  const getSyncSuccessMessage = (fieldCount, syncResults) => {
    const statusResult = syncResults.find(result => result?.field_id === 'status');
    const statusSuffix = statusResult
      ? `，工单状态包含 ${statusResult.option_count || 0} 个目标状态`
      : '';
    const warnings = syncResults.flatMap(result => result?.warnings || []);
    return [`同步完成：${fieldCount} 个字段${statusSuffix}`, ...warnings].join('；');
  };

  const handleSyncExistingFields = async () => {
    const selectedFieldIds = Array.from(new Set(selectedExistingFieldIds));
    if (selectedFieldIds.length === 0) {
      setAlertState({ isOpen: true, message: '请选择要同步的字段' });
      return;
    }
    if (!syncProjectKeys.trim()) {
      setAlertState({ isOpen: true, message: '请输入 Jira 项目 KEY' });
      return;
    }

    try {
      setSyncingFieldId('batch');
      const syncResults = [];
      for (const fieldId of selectedFieldIds) {
        syncResults.push(await syncSingleJiraField(fieldId));
      }
      await fetchFields();
      setShowExistingFieldSyncModal(false);
      setSelectedExistingFieldIds([]);
      setExistingFieldSearchKeyword('');
      setAlertState({
        isOpen: true,
        message: getSyncSuccessMessage(selectedFieldIds.length, syncResults)
      });
    } catch (err) {
      setAlertState({ isOpen: true, message: '同步失败: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setSyncingFieldId(null);
    }
  };

  const handleSyncNewFields = async () => {
    const selectedFieldIds = Array.from(new Set(selectedProjectFieldIds));
    if (selectedFieldIds.length === 0) {
      setAlertState({ isOpen: true, message: '请选择要同步的字段' });
      return;
    }
    if (!syncProjectKeys.trim()) {
      setAlertState({ isOpen: true, message: '请输入 Jira 项目 KEY' });
      return;
    }

    try {
      setSyncingFieldId('batch');
      const syncResults = [];
      for (const fieldId of selectedFieldIds) {
        syncResults.push(await syncSingleJiraField(fieldId));
      }
      await fetchFields();
      setShowNewFieldSyncModal(false);
      setSelectedProjectFieldIds([]);
      setProjectFields([]);
      setNewFieldSearchKeyword('');
      setAlertState({
        isOpen: true,
        message: getSyncSuccessMessage(selectedFieldIds.length, syncResults)
      });
    } catch (err) {
      setAlertState({ isOpen: true, message: '同步失败: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setSyncingFieldId(null);
    }
  };

  const handleUpdateField = async (e) => {
    e.preventDefault();
    if (!editingField || saving) return;

    setSaving(true);
    try {
      await api.updateField(editingField.id, editingField);
      setShowEditModal(false);
      setEditingField(null);
      fetchFields();
    } catch (err) {
      setAlertState({ isOpen: true, message: '保存字段失败: ' + err.message });
    } finally {
      setSaving(false);
    }
  };

  const normalizeOptionForEdit = (option) => {
    const base = option && typeof option === 'object' ? option : {};
    const textValue = typeof option === 'string' ? option : '';
    const value = base.value ?? base.name ?? textValue;
    return {
      ...base,
      id: base.id ?? '',
      value: value ?? '',
      label: base.label ?? value ?? ''
    };
  };

  const openOptionsModal = (field) => {
    setOptionsField(field);
    setOptionsDraft(Array.isArray(field.options) ? field.options.map(normalizeOptionForEdit) : []);
    setShowOptionsModal(true);
  };

  const closeOptionsModal = () => {
    setShowOptionsModal(false);
    setOptionsField(null);
    setOptionsDraft([]);
  };

  const handleOptionChange = (index, key, value) => {
    setOptionsDraft(optionsDraft.map((option, optionIndex) => (
      optionIndex === index ? { ...option, [key]: value } : option
    )));
  };

  const addOptionRow = () => {
    setOptionsDraft([...optionsDraft, { id: '', value: '', label: '' }]);
  };

  const removeOptionRow = (index) => {
    setOptionsDraft(optionsDraft.filter((_, optionIndex) => optionIndex !== index));
  };

  const cleanOptionsForSave = () => {
    return optionsDraft
      .map(option => {
        const value = String(option.value || '').trim();
        const label = String(option.label || value).trim();
        const id = option.id === null || option.id === undefined ? '' : String(option.id).trim();
        return {
          ...option,
          id: id || null,
          value,
          label
        };
      })
      .filter(option => option.value || option.label || option.id);
  };

  const handleSaveFieldOptions = async () => {
    if (!optionsField || saving) return;

    setSaving(true);
    try {
      const nextOptions = cleanOptionsForSave();
      await api.updateField(optionsField.id, { options: nextOptions });
      await fetchFields();
      closeOptionsModal();
      setAlertState({ isOpen: true, message: '字段选项已保存' });
    } catch (err) {
      setAlertState({ isOpen: true, message: '保存选项失败: ' + err.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleFieldStatus = async (field) => {
    try {
      const newStatus = !field.is_active;
      await api.updateField(field.id, { is_active: newStatus });
      setFields(fields.map(f => f.id === field.id ? { ...f, is_active: newStatus } : f));
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteField = async (fieldId) => {
    setConfirmState({
      isOpen: true,
      message: '确定要删除这个字段吗？',
      onConfirm: async () => {
        try {
          await api.deleteField(fieldId);
          setFields(fields.filter(field => field.id !== fieldId));
        } catch (err) {
          setError(err.message);
        }
        setConfirmState({ ...confirmState, isOpen: false });
      }
    });
  };

  const openEditModal = (field) => {
    setEditingField({ ...field });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingField(null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortedFields = () => {
    return [...fields].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (typeof aVal === 'boolean') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: '4px', fontSize: '10px' }}>⇅</span>;
    return <span style={{ marginLeft: '4px', fontSize: '10px' }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      custom: '自定义',
      standard: '标准',
      system: '系统'
    };
    return labels[category] || category;
  };

  const getCategoryBadgeClass = (category) => {
    switch (category) {
      case 'system': return 'badge-warning';
      case 'standard': return 'badge-info';
      default: return 'badge-default';
    }
  };

  const getFieldTypeLabel = (type) => {
    const labels = {
      string: '字符串',
      object: '对象',
      user: '用户',
      array: '数组',
      cascade: '级联',
      date: '日期',
      number: '数字'
    };
    return labels[type] || type;
  };

  const getFieldTypeBadgeClass = (type) => {
    switch (type) {
      case 'user': return 'badge-info';
      case 'array': return 'badge-success';
      case 'cascade': return 'badge-warning';
      case 'date': return 'badge-warning';
      case 'number': return 'badge-info';
      case 'object': return 'badge-default';
      default: return 'badge-default';
    }
  };

  const filterExistingFieldByKeyword = (field) => {
    const keyword = existingFieldSearchKeyword.trim().toLowerCase();
    if (!keyword) return true;
    return [
      field.field_id,
      field.display_name,
      field.id,
      field.name
    ].some(value => String(value || '').toLowerCase().includes(keyword));
  };

  const filterProjectFieldByKeyword = (field) => (
    matchesJiraFieldSearch(field, newFieldSearchKeyword)
  );

  const filteredExistingFields = fields.filter(filterExistingFieldByKeyword);
  const filteredProjectFields = projectFields.filter(filterProjectFieldByKeyword);

  if (error) {
    return (
      <ErrorState
        title="无法加载字段配置"
        description={error}
        onRetry={fetchFields}
      />
    );
  }

  return (
    <div className="configuration-list-page fields-directory-page">
      <PageHeader
        title="字段管理"
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowExistingFieldSyncModal(true)}
            >
              重新同步已有字段
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowNewFieldSyncModal(true)}
            >
              同步新的字段
            </button>
          </>
        }
      />

      {/* 字段列表 */}
      {loading ? (
        <div className="fields-table-skeleton">
          <TableSkeleton rowCount={8} columnCount={9} label="加载字段配置中" />
        </div>
      ) : (
        <section className="configuration-list-surface fields-directory-surface">
          <header className="configuration-list-surface-header">
            <div>
              <h2>字段目录</h2>
              <p>共 {fields.length} 个已配置字段，变更会影响规则中的字段读取和条件匹配。</p>
            </div>
            <span className="configuration-list-count">{fields.filter(field => field.is_active).length} 已启用</span>
          </header>
          <div className="configuration-list-surface-body">
            <div className="table-container fields-config-table-wrap" style={{ border: 'none', borderRadius: 0 }}>
              <table className="table fields-config-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>ID{renderSortIcon('id')}</th>
                    <th onClick={() => handleSort('field_id')} style={{ cursor: 'pointer' }}>字段ID{renderSortIcon('field_id')}</th>
                    <th onClick={() => handleSort('display_name')} style={{ cursor: 'pointer' }}>显示名{renderSortIcon('display_name')}</th>
                    <th onClick={() => handleSort('field_type')} style={{ cursor: 'pointer' }}>类型{renderSortIcon('field_type')}</th>
                    <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>分类{renderSortIcon('category')}</th>
                    <th>选项</th>
                    <th>描述</th>
                    <th onClick={() => handleSort('is_active')} style={{ cursor: 'pointer', width: '100px' }}>状态{renderSortIcon('is_active')}</th>
                    <th style={{ width: '150px' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.length === 0 ? (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: 'var(--space-10)' }}>
                        <div className="empty-state">
                          <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="9" y1="9" x2="15" y2="9"></line>
                            <line x1="9" y1="15" x2="15" y2="15"></line>
                          </svg>
                          <div>暂无字段数据</div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    getSortedFields().map(field => (
                      <tr key={field.id}>
                        <td>{field.id}</td>
                        <td>
                          <code style={{
                            background: 'var(--bg-tertiary)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {field.field_id}
                          </code>
                          {field.value_path && (
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                              路径: {field.value_path}
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{field.display_name}</div>
                        </td>
                        <td>
                          <span className={`badge ${getFieldTypeBadgeClass(field.field_type)}`}>
                            {getFieldTypeLabel(field.field_type)}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${getCategoryBadgeClass(field.category)}`}>
                            {getCategoryLabel(field.category)}
                          </span>
                        </td>
                        <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                          <button
                            type="button"
                            className="table-action-icon-button"
                            onClick={() => openOptionsModal(field)}
                            aria-label="编辑字段选项"
                            title="编辑字段选项"
                          >
                            <ListFilter size={17} aria-hidden="true" />
                          </button>
                        </td>
                        <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                          {field.description || '-'}
                        </td>
                        <td>
                          <label className="switch" title={field.is_active ? '点击禁用' : '点击启用'}>
                            <input
                              type="checkbox"
                              checked={field.is_active}
                              onChange={() => toggleFieldStatus(field)}
                            />
                            <span className="switch-slider"></span>
                          </label>
                        </td>
                        <td>
                          <div className="table-action-icon-list">
                            <button type="button" className="table-action-icon-button" onClick={() => openEditModal(field)} aria-label="编辑字段" title="编辑字段">
                              <Pencil size={17} aria-hidden="true" />
                            </button>
                            <button type="button" className="table-action-icon-button is-danger" onClick={() => deleteField(field.id)} aria-label="删除字段" title="删除字段">
                              <X size={19} aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="fields-mobile-list responsive-operation-list">
              {getSortedFields().map(field => (
                <article className="responsive-operation-row" key={field.id}>
                  <div className="responsive-operation-main">
                    <strong>{field.display_name}</strong>
                    <span>{field.field_id}</span>
                    {field.value_path && <small>路径: {field.value_path}</small>}
                  </div>
                  <div className="fields-mobile-badges">
                    <span className={`badge ${getFieldTypeBadgeClass(field.field_type)}`}>
                      {getFieldTypeLabel(field.field_type)}
                    </span>
                    <span className={`badge ${getCategoryBadgeClass(field.category)}`}>
                      {getCategoryLabel(field.category)}
                    </span>
                  </div>
                  <div className="responsive-operation-meta">
                    <span>选项 {Array.isArray(field.options) ? field.options.length : 0}</span>
                    <label className="switch" title={field.is_active ? '点击禁用' : '点击启用'}>
                      <input
                        type="checkbox"
                        checked={field.is_active}
                        onChange={() => toggleFieldStatus(field)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                    <div className="fields-mobile-actions">
                      <button type="button" className="table-action-icon-button" onClick={() => openOptionsModal(field)} aria-label="编辑字段选项" title="编辑字段选项"><ListFilter size={17} aria-hidden="true" /></button>
                      <button type="button" className="table-action-icon-button" onClick={() => openEditModal(field)} aria-label="编辑字段" title="编辑字段"><Pencil size={17} aria-hidden="true" /></button>
                      <button type="button" className="table-action-icon-button is-danger" onClick={() => deleteField(field.id)} aria-label="删除字段" title="删除字段"><X size={19} aria-hidden="true" /></button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 字段选项弹窗 */}
      {showOptionsModal && optionsField && (
        <Modal isOpen title={<>编辑选项 - {optionsField.display_name}</>} onClose={closeOptionsModal} busy={saving || Boolean(syncingFieldId) || projectFieldsLoading} size="large" className="admin-standard-dialog">


            <div className="modal-body">
              <div style={{ marginBottom: 'var(--space-3)', color: 'var(--text-secondary)', fontSize: '13px' }}>
                字段ID：<code>{optionsField.field_id}</code>
              </div>
              {optionsDraft.length === 0 ? (
                <div className="empty-state" style={{ padding: 'var(--space-6)' }}>
                  暂无选项
                </div>
              ) : (
                <div className="table-container" style={{ borderRadius: 'var(--radius-md)' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: '160px' }}>ID</th>
                        <th>值</th>
                        <th>显示名</th>
                        <th style={{ width: '90px' }}>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {optionsDraft.map((option, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              className="form-input"
                              value={option.id || ''}
                              onChange={(e) => handleOptionChange(index, 'id', e.target.value)}
                              placeholder="可选"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-input"
                              value={option.value || ''}
                              onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                              placeholder="JQL/规则使用的值"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-input"
                              value={option.label || ''}
                              onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                              placeholder="页面展示名称"
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => removeOptionRow(index)}
                            >
                              删除
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addOptionRow}
                style={{ marginTop: 'var(--space-3)' }}
              >
                添加选项
              </button>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={closeOptionsModal} disabled={saving}>
                取消
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveFieldOptions} disabled={saving}>
                保存选项
              </button>
            </div>

        </Modal>
      )}

      {/* 已有字段重新同步弹窗 */}
      {showExistingFieldSyncModal && (
        <Modal isOpen title={<>已有字段重新同步</>} onClose={() => setShowExistingFieldSyncModal(false)} busy={saving || Boolean(syncingFieldId) || projectFieldsLoading} size="large" className="admin-standard-dialog">


            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">项目 Key</label>
                  <input
                    type="text"
                    value={syncProjectKeys}
                    onChange={(e) => setSyncProjectKeys(e.target.value)}
                    className="form-input"
                    placeholder="项目 Key，如 OJML"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">参考工单 Key（可选）</label>
                  <input type="text" value={referenceIssueKey}
                    onChange={(e) => setReferenceIssueKey(e.target.value)}
                    className="form-input" placeholder="如 OJML-123，用于获取字段选项" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">工单类型</label>
                  <input
                    type="text"
                    value={syncIssueTypes}
                    onChange={(e) => setSyncIssueTypes(e.target.value)}
                    className="form-input"
                    placeholder="工单类型，如 Bug, Task"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">搜索已有字段</label>
                  <input
                    type="text"
                    value={existingFieldSearchKeyword}
                    onChange={(e) => setExistingFieldSearchKeyword(e.target.value)}
                    className="form-input"
                    placeholder="按字段名称或 ID 搜索"
                  />
                </div>
              </div>

              <div className="configuration-list-surface" style={{ margin: 0 }}>
                <div className="configuration-list-surface-body" style={{ padding: 0 }}>
                  <div className="table-container" style={{ border: 'none', borderRadius: 0, maxHeight: '420px', overflow: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th style={{ width: '44px' }}></th>
                          <th>字段名称</th>
                          <th>字段 ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredExistingFields.length === 0 ? (
                          <tr><td colSpan="3" style={{ textAlign: 'center' }}>暂无匹配字段</td></tr>
                        ) : filteredExistingFields.map(field => (
                          <tr key={field.field_id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedExistingFieldIds.includes(field.field_id)}
                                onChange={() => toggleSelectedValue(field.field_id, selectedExistingFieldIds, setSelectedExistingFieldIds)}
                              />
                            </td>
                            <td>{field.display_name || field.field_id}</td>
                            <td><code>{field.field_id}</code></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setShowExistingFieldSyncModal(false)} disabled={Boolean(syncingFieldId)}>
                关闭
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSyncExistingFields} disabled={Boolean(syncingFieldId)}>
                {syncingFieldId ? '同步中...' : `同步已选 (${selectedExistingFieldIds.length})`}
              </button>
            </div>

        </Modal>
      )}

      {/* 同步新的字段弹窗 */}
      {showNewFieldSyncModal && (
        <Modal isOpen title={<>同步新的字段</>} onClose={() => setShowNewFieldSyncModal(false)} busy={saving || Boolean(syncingFieldId) || projectFieldsLoading} size="large" className="admin-standard-dialog">


            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">项目 Key</label>
                  <input
                    type="text"
                    value={syncProjectKeys}
                    onChange={(e) => setSyncProjectKeys(e.target.value)}
                    className="form-input"
                    placeholder="项目 Key，如 OJML"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">参考工单 Key（可选）</label>
                  <input type="text" value={referenceIssueKey}
                    onChange={(e) => setReferenceIssueKey(e.target.value)}
                    className="form-input" placeholder="如 OJML-123，用于获取字段选项" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">工单类型</label>
                  <input
                    type="text"
                    value={syncIssueTypes}
                    onChange={(e) => setSyncIssueTypes(e.target.value)}
                    className="form-input"
                    placeholder="工单类型，如 Bug, Task"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">搜索 Jira 字段</label>
                  <input
                    type="text"
                    value={newFieldSearchKeyword}
                    onChange={(e) => setNewFieldSearchKeyword(e.target.value)}
                    className="form-input"
                    placeholder="按字段名称或 ID 搜索"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={loadProjectFields}
                  disabled={projectFieldsLoading || Boolean(syncingFieldId)}
                >
                  {projectFieldsLoading ? '拉取中...' : '拉取项目字段'}
                </button>
              </div>

              <div className="configuration-list-surface" style={{ margin: 0 }}>
                <div className="configuration-list-surface-body" style={{ padding: 0 }}>
                  <div className="table-container" style={{ border: 'none', borderRadius: 0, maxHeight: '420px', overflow: 'auto' }}>
                    <table className="table jira-project-fields-table">
                      <thead>
                        <tr>
                          <th style={{ width: '44px' }}></th>
                          <th>字段名称</th>
                          <th>字段 ID</th>
                          <th>状态</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProjectFields.length === 0 ? (
                          <tr><td colSpan="4" style={{ textAlign: 'center' }}>请先拉取项目字段</td></tr>
                        ) : filteredProjectFields.map(field => (
                          <tr key={field.id}>
                            <td className="jira-project-field-select">
                              <input
                                type="checkbox"
                                checked={selectedProjectFieldIds.includes(field.id)}
                                onChange={() => toggleSelectedValue(field.id, selectedProjectFieldIds, setSelectedProjectFieldIds)}
                              />
                            </td>
                            <td className="jira-project-field-name">{getJiraFieldDisplayName(field)}</td>
                            <td className="jira-project-field-id"><code>{field.id}</code></td>
                            <td className="jira-project-field-status">{field.is_existing ? <span className="badge badge-default">已存在</span> : <span className="badge badge-success">新字段</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setShowNewFieldSyncModal(false)} disabled={Boolean(syncingFieldId) || projectFieldsLoading}>
                关闭
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSyncNewFields} disabled={Boolean(syncingFieldId)}>
                {syncingFieldId ? '同步中...' : `同步已选 (${selectedProjectFieldIds.length})`}
              </button>
            </div>

        </Modal>
      )}

      {/* 编辑弹窗 */}
      {showEditModal && editingField && (
        <Modal isOpen title={<>编辑字段</>} onClose={closeEditModal} busy={saving || Boolean(syncingFieldId) || projectFieldsLoading} size="large" className="admin-standard-dialog">


            <form onSubmit={handleUpdateField}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">字段ID</label>
                  <code style={{
                    display: 'block',
                    background: 'var(--bg-tertiary)',
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '14px'
                  }}>
                    {editingField.field_id}
                  </code>
                  <small style={{ color: 'var(--text-muted)' }}>字段ID不可修改</small>
                </div>
                <div className="form-group">
                  <label className="form-label">显示名 *</label>
                  <input
                    type="text"
                    value={editingField.display_name}
                    onChange={(e) => setEditingField({...editingField, display_name: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">字段类型</label>
                  <SearchableSelect
                    value={editingField.field_type || 'string'}
                    options={[
                      { value: 'string', label: '字符串（简单值）' },
                      { value: 'object', label: '对象（含 value 字段）' },
                      { value: 'user', label: '用户（含 displayName）' },
                      { value: 'array', label: '数组（多选）' },
                      { value: 'cascade', label: '级联选择' },
                      { value: 'date', label: '日期' },
                      { value: 'number', label: '数字' }
                    ]}
                    onChange={(val) => setEditingField({...editingField, field_type: val})}
                    placeholder="选择字段类型"
                    searchable={false}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">值提取路径</label>
                  <input
                    type="text"
                    value={editingField.value_path || ''}
                    onChange={(e) => setEditingField({...editingField, value_path: e.target.value})}
                    className="form-input"
                    placeholder="如：value, displayName, child.value"
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    用于从复杂对象中提取值，如 "value"、"displayName"、"child.value"
                  </small>
                </div>
                <div className="form-group">
                  <label className="form-label">分类</label>
                  <SearchableSelect
                    value={editingField.category}
                    options={[
                      { value: 'custom', label: '自定义' },
                      { value: 'standard', label: '标准' },
                      { value: 'system', label: '系统' }
                    ]}
                    onChange={(val) => setEditingField({...editingField, category: val})}
                    placeholder="选择分类"
                    searchable={false}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">描述</label>
                  <input
                    type="text"
                    value={editingField.description || ''}
                    onChange={(e) => setEditingField({...editingField, description: e.target.value})}
                    className="form-input"
                    placeholder="字段描述（可选）"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editingField.is_active}
                      onChange={(e) => setEditingField({...editingField, is_active: e.target.checked})}
                    />
                    <span>启用字段</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={closeEditModal} disabled={saving}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  保存修改
                </button>
              </div>
            </form>

        </Modal>
      )}

      <Confirm
        isOpen={confirmState.isOpen}
        title="确认删除"
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={() => setConfirmState({ ...confirmState, isOpen: false })}
      />

      <Message
        type="info"
        message={alertState.isOpen ? alertState.message : ''}
        onClose={() => setAlertState({ ...alertState, isOpen: false })}
      />
    </div>
  );
};

export default FieldsPage;
